import { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Pen, Type, Eraser, Eye, ArrowLeft, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export default function SignatureModal({ isOpen, onClose, onSave, isBeschlussprotokoll = false, prefillName = '', prefillRole = '', prefillEmail = '' }) {
  const [signatureType, setSignatureType] = useState('drawn');
  const [typedSignature, setTypedSignature] = useState('');
  const [signatureFont, setSignatureFont] = useState('Caveat');
  const [email, setEmail] = useState(prefillEmail);
  const [signerName, setSignerName] = useState(prefillName);
  const [signerRole, setSignerRole] = useState(prefillRole);
  const [drawnSignaturePreview, setDrawnSignaturePreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const sigCanvas = useRef(null);

  // Available signature fonts
  const signatureFonts = [
    { name: 'Caveat', label: 'Elegant', style: 'Caveat, cursive' },
    { name: 'Dancing Script', label: 'Schreibschrift', style: "'Dancing Script', cursive" },
    { name: 'Pinyon Script', label: 'Kalligrafie', style: "'Pinyon Script', cursive" }
  ];

  // Update prefilled values when props change
  useEffect(() => {
    setSignerName(prefillName);
    setSignerRole(prefillRole);
    setEmail(prefillEmail);
  }, [prefillName, prefillRole, prefillEmail]);

  // Reset preview state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShowPreview(false);
      setPreviewData(null);
    }
  }, [isOpen]);

  // Update drawn signature preview when user draws
  const updateDrawnPreview = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setDrawnSignaturePreview(sigCanvas.current.toDataURL());
    } else {
      setDrawnSignaturePreview(null);
    }
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setDrawnSignaturePreview(null);
    }
  };

  const handleShowPreview = () => {
    if (!email) {
      alert('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }

    if (!signerName.trim()) {
      alert('Bitte geben Sie Ihren Namen ein');
      return;
    }

    if (isBeschlussprotokoll && !signerRole) {
      alert('Bitte wählen Sie Ihre Rolle aus');
      return;
    }

    let signatureData = '';

    if (signatureType === 'drawn') {
      if (sigCanvas.current.isEmpty()) {
        alert('Bitte erstellen Sie zuerst eine Unterschrift');
        return;
      }
      signatureData = sigCanvas.current.toDataURL();
    } else if (signatureType === 'typed') {
      if (!typedSignature.trim()) {
        alert('Bitte geben Sie Ihre Unterschrift ein');
        return;
      }
      signatureData = typedSignature;
    }

    // Store preview data and show preview
    setPreviewData({
      signatureData,
      signatureType,
      signatureFont: signatureType === 'typed' ? signatureFont : null,
      email,
      signerName,
      signerRole
    });
    setShowPreview(true);
  };

  const handleBackToEdit = () => {
    setShowPreview(false);
  };

  const handleConfirmAndSave = () => {
    if (previewData) {
      onSave(
        previewData.signatureData,
        previewData.signatureType,
        previewData.email,
        previewData.signerName,
        previewData.signerRole,
        previewData.signatureFont
      );
    }
  };

  const handleSave = () => {
    if (!email) {
      alert('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }

    if (!signerName.trim()) {
      alert('Bitte geben Sie Ihren Namen ein');
      return;
    }

    if (isBeschlussprotokoll && !signerRole) {
      alert('Bitte wählen Sie Ihre Rolle aus');
      return;
    }

    let signatureData = '';

    if (signatureType === 'drawn') {
      if (sigCanvas.current.isEmpty()) {
        alert('Bitte erstellen Sie zuerst eine Unterschrift');
        return;
      }
      signatureData = sigCanvas.current.toDataURL();
    } else if (signatureType === 'typed') {
      if (!typedSignature.trim()) {
        alert('Bitte geben Sie Ihre Unterschrift ein');
        return;
      }
      signatureData = typedSignature;
    }

    // Pass font info for typed signatures
    const fontInfo = signatureType === 'typed' ? signatureFont : null;
    onSave(signatureData, signatureType, email, signerName, signerRole, fontInfo);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="signature-modal">
        {!showPreview ? (
          // STEP 1: Signature Input
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Unterschrift erstellen
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5">
              {/* Name Field - Always Required */}
              <div>
                <Label htmlFor="signer-name">Ihr vollständiger Name *</Label>
                <Input
                  id="signer-name"
                  data-testid="signer-name-input"
                  type="text"
                  placeholder="Max Mustermann"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className="mt-2"
                />
                <p className="text-xs text-zinc-500 mt-1">Wird unter Ihrer Unterschrift angezeigt</p>
              </div>

              {/* Role Selection - Only for Beschlussprotokoll */}
              {isBeschlussprotokoll && (
                <div>
                  <Label htmlFor="signer-role">Ihre Rolle *</Label>
                  <Select value={signerRole} onValueChange={setSignerRole}>
                    <SelectTrigger className="mt-2" data-testid="signer-role-select">
                      <SelectValue placeholder="Bitte auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mitglied der Gemeinschaft">Mitglied der Gemeinschaft</SelectItem>
                      <SelectItem value="für den Verwaltungsbeirat">für den Verwaltungsbeirat</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1">Wird unter Ihrem Namen angezeigt</p>
                </div>
              )}

              <div>
                <Label htmlFor="signer-email">Ihre E-Mail-Adresse *</Label>
                <Input
                  id="signer-email"
                  data-testid="signer-email-input"
                  type="email"
                  placeholder="ihre.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2"
                />
              </div>

              <Tabs defaultValue="drawn" onValueChange={setSignatureType}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="drawn" data-testid="draw-tab" className="gap-2">
                    <Pen className="h-4 w-4" />
                    Zeichnen
                  </TabsTrigger>
                  <TabsTrigger value="typed" data-testid="type-tab" className="gap-2">
                    <Type className="h-4 w-4" />
                    Tippen
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="drawn" className="space-y-4">
                  <div className="border-2 border-zinc-200 rounded-lg overflow-hidden bg-white">
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        className: 'signature-canvas',
                        width: 600,
                        height: 150,
                        'data-testid': 'signature-canvas'
                      }}
                      penColor="#18181b"
                      onEnd={updateDrawnPreview}
                    />
                  </div>
                  <Button
                    data-testid="clear-signature-button"
                    onClick={clearSignature}
                    variant="outline"
                    className="w-full gap-2"
                  >
                    <Eraser className="h-4 w-4" />
                    Löschen
                  </Button>
                </TabsContent>

                <TabsContent value="typed" className="space-y-4">
                  <div>
                    <Label htmlFor="typed-name">Ihre Unterschrift</Label>
                    <Input
                      id="typed-name"
                      data-testid="typed-signature-input"
                      type="text"
                      placeholder="Unterschrift eingeben"
                      value={typedSignature}
                      onChange={(e) => setTypedSignature(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  
                  {/* Font Selection */}
                  <div>
                    <Label className="text-sm text-zinc-600">Schriftart wählen</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {signatureFonts.map((font) => (
                        <button
                          key={font.name}
                          type="button"
                          onClick={() => setSignatureFont(font.name)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            signatureFont === font.name 
                              ? 'border-zinc-900 bg-zinc-50' 
                              : 'border-zinc-200 hover:border-zinc-300'
                          }`}
                        >
                          <p 
                            className="text-xl truncate" 
                            style={{ fontFamily: font.style }}
                          >
                            {typedSignature || 'Beispiel'}
                          </p>
                          <p className="text-xs text-zinc-500 mt-1">{font.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {typedSignature && (
                    <div className="border-2 border-zinc-200 rounded-lg p-6 bg-white">
                      <p 
                        className="text-3xl text-center" 
                        style={{ fontFamily: signatureFonts.find(f => f.name === signatureFont)?.style }}
                      >
                        {typedSignature}
                      </p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            <DialogFooter>
              <Button data-testid="cancel-signature-button" onClick={onClose} variant="outline">
                Abbrechen
              </Button>
              <Button
                data-testid="preview-signature-button"
                onClick={handleShowPreview}
                className="bg-zinc-950 text-white hover:bg-zinc-800 gap-2"
              >
                <Eye className="h-4 w-4" />
                Vorschau anzeigen
              </Button>
            </DialogFooter>
          </>
        ) : (
          // STEP 2: Preview & Confirmation
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Unterschrift bestätigen
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Bitte überprüfen Sie Ihre Unterschrift.</strong> Nach der Bestätigung wird sie im Dokument gespeichert.
                </p>
              </div>

              {/* Signature Preview Card */}
              <div className="border-2 border-zinc-300 rounded-xl p-6 bg-white shadow-sm">
                <p className="text-xs text-zinc-500 mb-4 uppercase tracking-wide font-medium">So wird Ihre Unterschrift im Dokument erscheinen:</p>
                
                <div className="bg-gradient-to-br from-zinc-50 to-white border border-zinc-200 rounded-lg p-6">
                  {/* Signature */}
                  <div className="min-h-[60px] flex items-end pb-2 border-b-2 border-zinc-300 mb-4">
                    {previewData?.signatureType === 'drawn' ? (
                      <img 
                        src={previewData.signatureData} 
                        alt="Unterschrift" 
                        className="max-h-[55px] object-contain"
                      />
                    ) : (
                      <p 
                        className="text-3xl font-medium text-zinc-800" 
                        style={{ fontFamily: signatureFonts.find(f => f.name === previewData?.signatureFont)?.style }}
                      >
                        {previewData?.signatureData}
                      </p>
                    )}
                  </div>
                  
                  {/* Name, Role, and Certification */}
                  <div className="space-y-1">
                    <p className="text-sm text-zinc-800 font-semibold">{previewData?.signerName}</p>
                    {isBeschlussprotokoll && previewData?.signerRole && (
                      <p className="text-xs text-zinc-600">{previewData.signerRole}</p>
                    )}
                    <p className="text-[10px] text-zinc-400 pt-2">
                      Unterschrieben via SignFlow am {new Date().toLocaleDateString('de-DE')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Details Summary */}
              <div className="bg-zinc-50 rounded-lg p-4 space-y-2">
                <h4 className="text-sm font-semibold text-zinc-700">Ihre Angaben:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-zinc-500">Name:</span>
                    <span className="ml-2 font-medium">{previewData?.signerName}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">E-Mail:</span>
                    <span className="ml-2 font-medium">{previewData?.email}</span>
                  </div>
                  {isBeschlussprotokoll && (
                    <div>
                      <span className="text-zinc-500">Rolle:</span>
                      <span className="ml-2 font-medium">{previewData?.signerRole}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-zinc-500">Typ:</span>
                    <span className="ml-2 font-medium">{previewData?.signatureType === 'drawn' ? 'Gezeichnet' : 'Getippt'}</span>
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Hinweis:</strong> Mit dem Absenden bestätigen Sie, dass dies Ihre rechtsgültige Unterschrift ist.
                </p>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button 
                data-testid="back-to-edit-button" 
                onClick={handleBackToEdit} 
                variant="outline"
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück bearbeiten
              </Button>
              <Button
                data-testid="confirm-signature-button"
                onClick={handleConfirmAndSave}
                className="bg-green-600 text-white hover:bg-green-700 gap-2"
              >
                <Send className="h-4 w-4" />
                Unterschrift absenden
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}